import React from 'react';
import { Helmet } from 'react-helmet-async';

const getArticleFaqSchema = (pageUrl) => {
  if (pageUrl !== '/beginners-complete-guide-to-omakase') return null;

  const faqs = [
    ['第一次吃 Omakase 需要預約嗎？', '是的，Omakase 餐廳通常需要提前預約。因為廚師需要根據預約人數準備食材，且座位有限。建議至少提前 1-2 週預約，熱門餐廳可能需要提前 1-3 個月。預約時請告知人數、用餐時間、是否有飲食禁忌等資訊。'],
    ['Omakase 的價格範圍大概是多少？', 'Omakase 價格範圍很廣：入門級約 NT$1,500-3,000，中階級約 NT$3,000-6,000，高階級約 NT$6,000-12,000，頂級可達 NT$12,000 以上。價格差異主要來自食材等級、廚師經驗和餐廳定位。'],
    ['用餐時可以拍照嗎？', '大多數餐廳允許拍照，但請遵守：關閉閃光燈和快門聲、不影響其他客人用餐、避免拍攝其他客人、不讓拍照影響用餐節奏。某些高級餐廳可能禁止拍照，請事先詢問。'],
    ['如果有食材過敏或不吃的東西怎麼辦？', '請在預約時就告知餐廳您的飲食限制或過敏原。廚師會根據您的需求調整菜單。但請注意，過多的限制可能會影響 Omakase 的體驗完整性。'],
    ['Omakase 用餐時間大約多長？', '用餐時間依價位和類型而異：入門級約 1.5 小時，中階級約 2 小時，高階級約 2.5 小時，頂級 3 小時以上。壽司 Omakase 通常較短（1.5-2小時），懷石 Omakase 較長（2-3小時）。'],
    ['需要給小費嗎？', '在台灣，Omakase 餐廳通常已包含服務費，不需要額外給小費。如果對服務特別滿意，最好的感謝方式是真誠的讚美、下次再訪，以及在社群媒體上分享正面評價。'],
    ['可以帶小孩去嗎？', '這取決於餐廳政策和孩子的年齡。部分高級餐廳有年齡限制（如 12 歲以上）。需考慮孩子是否能安靜用餐 2-3 小時。預約時務必告知有孩童同行。'],
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `https://omakase.tw${pageUrl}#faq`,
    mainEntity: faqs.map(([name, text]) => ({
      '@type': 'Question',
      name,
      acceptedAnswer: {
        '@type': 'Answer',
        text,
      },
    })),
  };
};

/**
 * 統一 SEO 組件 v3.0 - 2026-03-07
 * ✅ 修正：新增 breadcrumb prop 處理（ArticleListPage 的 Breadcrumb Schema 不再靜默丟失）
 * ✅ 修正：breadcrumb 與 schema 合併為單一 @graph 輸出，避免多個 <script> 衝突
 * ✅ 保留：author meta、article:publisher、structuredData 兼容
 */
function SEO({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  schema,
  structuredData,
  breadcrumb,
  article,
  noindex = false,
}) {
  const SITE_NAME     = 'お任せ Omakase';
  const SITE_URL      = 'https://omakase.tw';
  const TWITTER_SITE  = '@omakase_tw';
  const ORG_URL       = SITE_URL;
  const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1579952363873-27d3bfad9c0d?w=1200&h=630&fit=crop';

  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const fullUrl   = url ? `${SITE_URL}${url}` : SITE_URL;
  const ogImage   = image || DEFAULT_IMAGE;

  // ✅ 核心修正：合併 schema + breadcrumb 為單一 @graph，避免多個 <script>
  const buildFinalSchema = () => {
    const base = schema || structuredData;
    if (!base && !breadcrumb) return null;

    // 如果已是 @graph 格式（ArticlePage 情況），直接使用
    if (base?.['@graph'] && !breadcrumb) return base;

    // 如果有 breadcrumb 需要合併（ArticleListPage 情況）
    const strip = (s) => {
      if (!s) return null;
      const { '@context': _, ...rest } = s;
      return rest;
    };

    const graphNodes = [];
    if (base)       graphNodes.push(strip(base));
    if (breadcrumb) graphNodes.push(strip(breadcrumb));

    if (graphNodes.length === 1 && !breadcrumb) return base;

    return {
      '@context': 'https://schema.org',
      '@graph': graphNodes.filter(Boolean),
    };
  };

  const baseSchema = buildFinalSchema();
  const articleFaqSchema = getArticleFaqSchema(url);
  const finalSchema = articleFaqSchema && baseSchema?.['@graph']
    ? {
      ...baseSchema,
      '@graph': [
        ...baseSchema['@graph'],
        (() => {
          const { '@context': _, ...faqSchema } = articleFaqSchema;
          return faqSchema;
        })(),
      ],
    }
    : baseSchema || articleFaqSchema;

  return (
    <Helmet>
      {/* ── 基礎 Meta ── */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords    && <meta name="keywords"    content={keywords} />}
      {article?.author && <meta name="author" content={article.author} />}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      <meta name="language" content="zh-TW" />
      <link rel="canonical" href={fullUrl} />

      {/* ── Open Graph ── */}
      <meta property="og:type"         content={type} />
      <meta property="og:site_name"    content={SITE_NAME} />
      <meta property="og:title"        content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url"          content={fullUrl} />
      <meta property="og:image"        content={ogImage} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt"    content={title || SITE_NAME} />
      <meta property="og:locale"       content="zh_TW" />

      {/* ── Article-specific OG ── */}
      {article?.publishDate  && <meta property="article:published_time"  content={article.publishDate} />}
      {article?.modifiedDate && <meta property="article:modified_time"   content={article.modifiedDate} />}
      {article?.author       && <meta property="article:author"          content={article.author} />}
      {article?.section      && <meta property="article:section"         content={article.section} />}
      {type === 'article'    && <meta property="article:publisher"       content={ORG_URL} />}
      {article?.tags?.map(tag => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* ── Twitter Cards ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_SITE} />
      <meta name="twitter:creator"     content={TWITTER_SITE} />
      <meta name="twitter:title"       content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image"       content={ogImage} />
      <meta name="twitter:image:alt"   content={title || SITE_NAME} />

      {/* ── JSON-LD Schema（單一 <script>，schema + breadcrumb 合併輸出）── */}
      {finalSchema && (
        <script type="application/ld+json">
          {JSON.stringify(finalSchema, null, 0)}
        </script>
      )}
    </Helmet>
  );
}

export default SEO;